import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import { createPortal } from "react-dom";
import "./weekModal.css";
import "../../AdminPages/CalendarPage/calendarPage.css";
import "../../../../utils/tentativeShimmer.css";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HearingIcon from "@mui/icons-material/Hearing";
import EventDetailPopup from "../EventDetailPopup/EventDetailPopup";
import EventFilterToggle from "../EventFilterToggle/EventFilterToggle";
import AllDayEventStrip from "../AllDayEventStrip/AllDayEventStrip";
import {
  applyEventFilter,
  isAllDayItem,
  allDayItemsForDay,
} from "../../../../utils/calendarPersonalEvents";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import AIAssignmentPanel from "../AIAssignmentPanel/AIAssignmentPanel";
import EditEventModal from "../EditEventModal/EditEventModal";
import CreateEventModal from "../CreateEventModal/CreateEventModal";
import ViewClientInvoiceModal from "../ViewClientInvoiceModal/ViewClientInvoiceModal";
import ViewClientQuoteModal from "../ViewClientQuoteModal/ViewClientQuoteModal";
import JobFormReviewModal from "../JobFormReviewModal/JobFormReviewModal";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import { computeLaneLayout, getBlockLabelPrefix, getBlockLabelName, personalEventColor } from "../../../../utils/calendarLaneLayout";
import { getCalendarJobCategory } from "../../../../utils/calendarJobCategory";
import { getFinisherRange } from "../../../../utils/calendarSplitBetween";
import { useCalendarHoverHighlight } from "../../../../utils/useCalendarHoverHighlight";
import "../../../../utils/vacationStripes.css";
import { splitEventForDay } from "../../../../utils/calendarEventSegments";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import { useLazyQuery } from "@apollo/client";
import { GET_ZONE_WEATHER_FOR_RANGE } from "../../../../utils/GraphQL/queries";
import WeatherChip from "../../../../components/WeatherChip/WeatherChip";
import { buildWeatherLookup, weatherForJob } from "../../../../utils/weatherZoneJoin";
import MultiDayBanner from "../shared/MultiDayBanner/MultiDayBanner";
import CurrentTimeLine from "../CurrentTimeLine/CurrentTimeLine";
import { useTourAction } from "../../../../tours/tourActions";
// import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

const HOUR_HEIGHT = 30;
const GRID_START_HOUR = 6;
const GRID_HOURS = 24;
const TOTAL_GRID_HEIGHT = GRID_HOURS * HOUR_HEIGHT;
const HOUR_LABEL_WIDTH = 40;
const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MOBILE_BREAKPOINT = 600;
const MOBILE_DAY_COUNT = 3;
// Weather (weather-by-zone, 2026-06-22) is cached per weekStart for 45 min and
// is intentionally DECOUPLED from the calendar_update SSE refetch — a job
// moving doesn't change the forecast, so weather must not refetch on every SSE.
const WEATHER_TTL_MS = 45 * 60 * 1000;

/** Lightweight hook: true when viewport is below the mobile breakpoint */
const useIsMobile = (breakpoint = MOBILE_BREAKPOINT) => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
};

const formatHourLabel = (hour) => {
  const h = hour % 24;
  if (h === 0) return "12a";
  if (h === 12) return "12p";
  if (h < 12) return `${h}a`;
  return `${h - 12}p`;
};

// Returns the displayed dollar amount for a calendar event block — the
// invoice total with tip line items stripped, falling back to the raw
// quote total for quote-only events. Refinishers + admins manually verify
// pay by reading this number × payPercentage; tips go to a separate
// full-pay bucket server-side, so leaving them in the displayed total
// over-counts the manual math. Returns null on $0-net invoices so the
// pill suppresses itself (semantic shift from "$0.00" prior — see
// WEB_CHANGELOG entry 2026-05-08).
const getJobAmountStr = (item) => {
  if (item.invoice?.invoiceItems) {
    const totalCents = parseInt(item.invoice.invoiceTotal) || 0;
    const tipCents = (item.invoice.invoiceItems || []).reduce((sum, it) => {
      if ((it.itemName || "").trim().toLowerCase() !== "tip") return sum;
      return sum + (parseInt(it.price) || 0) * (parseInt(it.quantity) || 1);
    }, 0);
    const displayCents = Math.max(totalCents - tipCents, 0);
    return displayCents > 0 ? priceStrFormatter(String(displayCents)) : null;
  }
  // Quote fallback: quotes never carry tips so subtraction is a no-op.
  const cents = item.quote?.quoteTotal;
  return cents ? priceStrFormatter(cents) : null;
};

// Min/Max finisher chip (bottom-left of event blocks): `getFinisherRange` is
// imported from utils/calendarSplitBetween.js — the doc's splitBetween, or a
// tentative placeholder's own number (2026-10-29).

const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i).toLocaleDateString("en-US", { month: "long" })
);

// Whether today falls within [rangeStart, rangeStart + dayCount) at
// day-level granularity. Drives `CurrentTimeLine.visible` so the line is
// hidden when the user navigates to a week or 3-day window that isn't
// the current one.
const isTodayInRange = (rangeStart, dayCount) => {
  if (!rangeStart) return false;
  const now = new Date();
  for (let i = 0; i < dayCount; i++) {
    const d = new Date(rangeStart);
    d.setDate(d.getDate() + i);
    if (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    ) {
      return true;
    }
  }
  return false;
};

// Personal ("SIX") events, 2026-09-11: `isAdmin` gates the A|P|W filter, the
// SIX button and the `includePersonal` fetch opt-in; `eventFilter` +
// `onEventFilterChange` are owned by CalendarPage so the three surfaces share
// one persisted value.
const WeekModal = ({
  open,
  onClose,
  currentMonth,
  setCurrentMonth,
  getScheduleRange,
  onSwitchToMonth,
  onSwitchToDay,
  isAdmin = false,
  eventFilter = "all",
  onEventFilterChange,
  /*
   * Deep-link, both OPTIONAL and both no-ops when absent (2026-10-07).
   *
   * The Tasks console's Avail. Sooner list hands an event to the calendar
   * rather than opening a detail popup of its own: that popup's Edit button is
   * unguarded and every host of it also has to render EditEventModal plus the
   * invoice and quote modals. This modal already hosts all of them, so the
   * cheapest correct answer is to send the user here.
   *
   * `initialDayMs` picks the week; `focusEventId` selects the event inside it
   * once the fetch lands, which is why it cannot be done at open time.
   */
  initialDayMs = null,
  focusEventId = null,
}) => {
  // const { handleSnack } = useSnackbar();
  const isMobile = useIsMobile();
  const [weekStart, setWeekStart] = useState(null);
  const [threeDayStart, setThreeDayStart] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  /* One-shot latch for `focusEventId` — see fetchWeekData. Reset whenever a
     NEW focus arrives, so a second deep-link in the same session still opens. */
  const focusConsumedRef = useRef(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewQuote, setViewQuote] = useState(null);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [createEventStartMs, setCreateEventStartMs] = useState("");
  const [createEventEndMs, setCreateEventEndMs] = useState("");
  // "job" from Create / grid click; "personal" from the SIX button.
  const [createEventType, setCreateEventType] = useState("job");
  const [hoveredHour, setHoveredHour] = useState(null);
  // Chain-aware hover spotlight — dims every other block and lights up the
  // whole chain when one chained event is hovered.
  const { onEnter: onHoverEnter, onLeave: onHoverLeave, styleFor: hoverStyleFor, isHoverActive } = useCalendarHoverHighlight();
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewInvoiceId, setReviewInvoiceId] = useState(null);

  // Picker state for month/year dropdowns
  const [pickerOpen, setPickerOpen] = useState(null); // null | 'month' | 'year'
  const pickerRef = useRef(null);
  const yearPickerRef = useRef(null);

  const gridBodyRef = useRef(null);

  // Track previous open state to detect open transitions
  const prevOpenRef = useRef(false);

  // --- Weather-by-zone state (decoupled from the schedule/SSE refetch) ---
  // weatherByZone: result of getZoneWeatherForRange for the visible week.
  // weatherCacheRef: { [weekKey]: { data, ts } } — in-memory TTL cache so
  // navigating away and back (or an SSE schedule refetch) reuses the forecast
  // instead of re-hitting the network.
  const [zoneWeather, setZoneWeather] = useState(null);
  const weatherCacheRef = useRef({});
  const [getZoneWeather] = useLazyQuery(GET_ZONE_WEATHER_FOR_RANGE);

  // Init week only when modal first opens (not on every currentMonth change)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!open || wasOpen) return; // Only run on open transition (false → true)

    const now = new Date();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    let refDate;
    if (initialDayMs) {
      // A deep-link names the day outright, so neither "today" nor "the 1st"
      // applies — it wins over both branches below.
      refDate = new Date(Number(initialDayMs));
    } else if (now.getFullYear() === year && now.getMonth() === month) {
      refDate = now;
    } else {
      refDate = new Date(year, month, 1);
    }

    const sunday = new Date(refDate);
    sunday.setDate(sunday.getDate() - sunday.getDay());
    sunday.setHours(0, 0, 0, 0);
    setWeekStart(sunday);

    // Initialize 3-day window for mobile: snap to nearest 3-day group within the week
    const dayOfWeek = refDate.getDay(); // 0=Sun
    const groupStart = dayOfWeek - (dayOfWeek % MOBILE_DAY_COUNT); // 0, 3, or 6
    const threeDayDate = new Date(sunday);
    threeDayDate.setDate(threeDayDate.getDate() + groupStart);
    threeDayDate.setHours(0, 0, 0, 0);
    setThreeDayStart(threeDayDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch data when week changes
  useEffect(() => {
    if (open && weekStart) {
      fetchWeekData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, weekStart]);

  // Fetch weather ONCE per week (cached 45 min). Separate from fetchWeekData /
  // the SSE refetch so a job moving never re-pulls the forecast. On a cache hit
  // within TTL we reuse the stored result; otherwise we fetch and store it.
  // A weather failure is swallowed (chip just doesn't render) — it never blocks
  // or errors the calendar.
  useEffect(() => {
    if (!open || !weekStart) return;
    const weekKey = `${weekStart.getFullYear()}-${weekStart.getMonth()}-${weekStart.getDate()}`;
    const cached = weatherCacheRef.current[weekKey];
    if (cached && Date.now() - cached.ts < WEATHER_TTL_MS) {
      setZoneWeather(cached.data);
      return;
    }
    const startMs = weekStart.getTime().toString();
    const wEnd = new Date(weekStart);
    wEnd.setDate(wEnd.getDate() + 6);
    wEnd.setHours(23, 59, 59, 999);
    const endMs = wEnd.getTime().toString();

    let cancelled = false;
    (async () => {
      try {
        const { data } = await getZoneWeather({
          variables: { startMs, endMs },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        const result = data?.getZoneWeatherForRange || [];
        weatherCacheRef.current[weekKey] = { data: result, ts: Date.now() };
        setZoneWeather(result);
      } catch (err) {
        // Non-fatal: degrade to no chips, never surface an error in the grid.
        console.error("Error fetching zone weather:", err);
        if (!cancelled) setZoneWeather(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, weekStart]);

  const fetchWeekData = async () => {
    setLoading(true);
    setError(null);
    setSelectedItem(null);

    const rangeStart = weekStart.getTime().toString();
    const satEnd = new Date(weekStart);
    satEnd.setDate(satEnd.getDate() + 6);
    satEnd.setHours(23, 59, 59, 999);
    const rangeEnd = satEnd.getTime().toString();

    try {
      const { data } = await getScheduleRange({
        variables: { rangeStart, rangeEnd, onlyGetInvoiced: false, includePersonal: isAdmin },
        fetchPolicy: "network-only",
      });
      const results = (data?.getScheduleRange || []).filter(
        (r) => r?.calendarItem?.startMs
      );
      results.sort(
        (a, b) =>
          parseInt(a.calendarItem.startMs) - parseInt(b.calendarItem.startMs)
      );
      setItems(results);

      /*
       * Open the detail popup for a deep-linked event, once we actually have
       * it. Matched by id against THIS fetch rather than held as a pending
       * promise: if the event is not in the loaded week (deleted, or moved out
       * of range since the link was made) nothing opens, which is the right
       * outcome — better than a popup over a stale row.
       *
       * CONSUMED ONCE. `fetchWeekData` re-runs on every weekStart change, so
       * without the ref, closing the popup and navigating away and back would
       * silently re-open it — for the whole life of the calendar modal. The
       * intent is "show me this one event now", not a standing instruction.
       * (It deliberately lives here and not in `silentRefetchWeekData`, so an
       * SSE calendar_update never re-opens a popup the user just closed.)
       */
      if (focusEventId && !focusConsumedRef.current) {
        const match = results.find(
          (r) => String(r?.calendarItem?.id) === String(focusEventId)
        );
        if (match) {
          focusConsumedRef.current = true;
          setSelectedItem(match);
        }
      }
    } catch (err) {
      console.error("Error fetching week schedule:", err);
      setError("Error loading schedule");
    }
    setLoading(false);
  };

  // SSE: silently refetch week data without loading spinner, preserve selected item
  const silentRefetchWeekData = useCallback(async () => {
    if (!open || !weekStart) return;

    const rangeStart = weekStart.getTime().toString();
    const satEnd = new Date(weekStart);
    satEnd.setDate(satEnd.getDate() + 6);
    satEnd.setHours(23, 59, 59, 999);
    const rangeEnd = satEnd.getTime().toString();

    try {
      const { data } = await getScheduleRange({
        variables: { rangeStart, rangeEnd, onlyGetInvoiced: false, includePersonal: isAdmin },
        fetchPolicy: "network-only",
      });
      const results = (data?.getScheduleRange || []).filter(
        (r) => r?.calendarItem?.startMs
      );
      results.sort(
        (a, b) =>
          parseInt(a.calendarItem.startMs) - parseInt(b.calendarItem.startMs)
      );
      setItems(results);

      // Update selected item with fresh data (or close popup if deleted)
      setSelectedItem((prev) => {
        if (!prev) return null;
        return results.find((r) => r.calendarItem.id === prev.calendarItem.id) || null;
      });
    } catch (err) {
      console.error("SSE week refetch error:", err);
    }
  }, [open, weekStart, getScheduleRange, isAdmin]);

  useSSEListener("calendar_update", silentRefetchWeekData);

  // Render-time All | Personal | Work filter + all-day split. Never a refetch:
  // `items` stays the full admin payload and each memo below derives from
  // `visibleItems`. All-day (noon-anchored, no endMs) items are pulled out
  // BEFORE bucketing so they never reach the lane layout as hour blocks.
  const visibleItems = useMemo(
    () => applyEventFilter(items, eventFilter),
    [items, eventFilter]
  );
  const timedItems = useMemo(
    () => visibleItems.filter((it) => !isAllDayItem(it)),
    [visibleItems]
  );

  // Auto-scroll to first timed job (an all-day noon anchor must not drag the
  // grid to 12p).
  useEffect(() => {
    if (!loading && timedItems.length > 0 && gridBodyRef.current) {
      const firstMs = parseInt(timedItems[0].calendarItem.startMs);
      if (!isNaN(firstMs)) {
        const dt = new Date(firstMs);
        const hourOffset = dt.getHours() + dt.getMinutes() / 60 - GRID_START_HOUR;
        gridBodyRef.current.scrollTop = Math.max(0, hourOffset * HOUR_HEIGHT - 40);
      }
    }
  }, [loading, timedItems]);

  const handlePrevWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      // Sync currentMonth so month view stays aligned
      setCurrentMonth?.(new Date(d.getFullYear(), d.getMonth(), 1));
      return d;
    });
  };

  const handleNextWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      // Sync currentMonth so month view stays aligned
      setCurrentMonth?.(new Date(d.getFullYear(), d.getMonth(), 1));
      return d;
    });
  };

  // --- 3-day mobile navigation (separate from week nav) ---
  const handlePrevThreeDays = () => {
    setThreeDayStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - MOBILE_DAY_COUNT);
      // If we've moved before the current weekStart, shift weekStart back too
      if (d < weekStart) {
        const newSunday = new Date(d);
        newSunday.setDate(newSunday.getDate() - newSunday.getDay());
        newSunday.setHours(0, 0, 0, 0);
        setWeekStart(newSunday);
      }
      setCurrentMonth?.(new Date(d.getFullYear(), d.getMonth(), 1));
      return d;
    });
  };

  const handleNextThreeDays = () => {
    setThreeDayStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + MOBILE_DAY_COUNT);
      // If we've moved past the current week (Sun+6=Sat), shift weekStart forward
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      if (d > weekEnd) {
        const newSunday = new Date(d);
        newSunday.setDate(newSunday.getDate() - newSunday.getDay());
        newSunday.setHours(0, 0, 0, 0);
        setWeekStart(newSunday);
      }
      setCurrentMonth?.(new Date(d.getFullYear(), d.getMonth(), 1));
      return d;
    });
  };

  const getThreeDayHeaderLabel = () => {
    if (!threeDayStart) return "";
    const end = new Date(threeDayStart);
    end.setDate(end.getDate() + MOBILE_DAY_COUNT - 1);
    const fmt = (d) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(threeDayStart)} - ${fmt(end)}`;
  };

  // Click-outside to close pickers
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  // Auto-scroll year picker to active year
  useEffect(() => {
    if (pickerOpen === "year" && yearPickerRef.current) {
      const active = yearPickerRef.current.querySelector(".active");
      if (active) active.scrollIntoView({ block: "center" });
    }
  }, [pickerOpen]);

  const togglePicker = (type) => {
    setPickerOpen((prev) => (prev === type ? null : type));
  };

  const handlePickMonth = (monthIdx) => {
    const year = weekStart ? weekStart.getFullYear() : new Date().getFullYear();
    const newMonth = new Date(year, monthIdx, 1);
    setCurrentMonth?.(newMonth);
    // Jump week to first of that month
    const sunday = new Date(newMonth);
    sunday.setDate(sunday.getDate() - sunday.getDay());
    sunday.setHours(0, 0, 0, 0);
    setWeekStart(sunday);
    setPickerOpen(null);
  };

  const handlePickYear = (year) => {
    const month = weekStart ? weekStart.getMonth() : new Date().getMonth();
    const newMonth = new Date(year, month, 1);
    setCurrentMonth?.(newMonth);
    const sunday = new Date(newMonth);
    sunday.setDate(sunday.getDate() - sunday.getDay());
    sunday.setHours(0, 0, 0, 0);
    setWeekStart(sunday);
    setPickerOpen(null);
  };

  // Derive display month/year from weekStart (mid-week Thursday determines the month)
  const displayMonth = weekStart ? weekStart.getMonth() : new Date().getMonth();
  const displayYear = weekStart ? weekStart.getFullYear() : new Date().getFullYear();

  const getHeaderLabel = () => {
    if (!weekStart) return "";
    const sun = weekStart;
    const sat = new Date(weekStart);
    sat.setDate(sat.getDate() + 6);
    const fmt = (d) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(sun)} - ${fmt(sat)}, ${sat.getFullYear()}`;
  };

  // Bucket items into 7 days using splitEventForDay so multi-day events
  // appear on every day they span. Each segment carries the original
  // item payload (for tap-through) but its calendarItem.startMs/endMs
  // are replaced with the day-clamped values for lane layout. _isFirstDay
  // / _isLastDay / _totalWorkDays are attached for multi-day chevron UI.
  const dayBuckets = useMemo(() => {
    if (!weekStart) return Array(7).fill([]);
    const buckets = Array.from({ length: 7 }, () => []);

    for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
      const dayDate = new Date(
        weekStart.getFullYear(),
        weekStart.getMonth(),
        weekStart.getDate() + dayIdx
      );
      const dayStartMs = dayDate.getTime();
      // End of THIS local day, not start + 24h (2026-09-20). On the 25-hour
      // fall-back day a fixed 24h window ends at 23:00, so an event starting
      // 23:00-23:59 falls in no column at all; on the 23-hour spring-forward
      // day it spills an hour into the next day and the event renders twice.
      const dayEndMs =
        new Date(
          weekStart.getFullYear(),
          weekStart.getMonth(),
          weekStart.getDate() + dayIdx + 1
        ).getTime() - 1;

      for (const item of timedItems) {
        const seg = splitEventForDay(item.calendarItem, dayStartMs, dayEndMs);
        if (!seg) continue;
        buckets[dayIdx].push({
          ...item,
          calendarItem: {
            ...item.calendarItem,
            startMs: seg.segStartMs,
            endMs: seg.segEndMs,
          },
          _originalItem: item,
          _isMultiDayPart: seg.totalWorkDays > 1,
          _isFirstDay: seg.isFirstDay,
          _isLastDay: seg.isLastDay,
          _workDayIndex: seg.workDayIndex,
          _totalWorkDays: seg.totalWorkDays,
        });
      }
    }
    return buckets;
  }, [timedItems, weekStart]);

  // All-day (personal "SIX") chips per weekday, keyed on the LOCAL day of the
  // noon anchor; already filtered + sorted. Feeds AllDayEventStrip.
  const allDayByDay = useMemo(() => {
    if (!weekStart) return Array.from({ length: 7 }, () => []);
    return Array.from({ length: 7 }, (_, dayIdx) =>
      allDayItemsForDay(
        visibleItems,
        new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + dayIdx)
      )
    );
  }, [visibleItems, weekStart]);

  // Compute lane-stacked blocks for each day (one block per item, no duplication)
  const blocks = useMemo(() => {
    if (!weekStart) return [];
    const result = [];

    dayBuckets.forEach((dayItems, dayIdx) => {
      if (dayItems.length === 0) return;

      const dayDate = new Date(weekStart);
      dayDate.setDate(dayDate.getDate() + dayIdx);
      const gridOriginMs = new Date(
        dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), GRID_START_HOUR
      ).getTime();

      const laneBlocks = computeLaneLayout(dayItems, gridOriginMs, HOUR_HEIGHT, 18);
      laneBlocks.forEach((lb) => result.push({ ...lb, dayIdx }));
    });

    return result;
  }, [dayBuckets, weekStart]);

  // --- 3-day mobile: compute visible day indices and blocks ---
  const threeDayIndices = useMemo(() => {
    if (!threeDayStart || !weekStart) return [0, 1, 2];
    const offset = Math.round((threeDayStart.getTime() - weekStart.getTime()) / 86400000);
    return [0, 1, 2].map((i) => offset + i);
  }, [threeDayStart, weekStart]);

  const blocksThreeDay = useMemo(() => {
    if (!weekStart || !threeDayStart) return [];
    const result = [];

    threeDayIndices.forEach((srcDayIdx, displayIdx) => {
      const dayItems = dayBuckets[srcDayIdx] || [];
      if (dayItems.length === 0) return;

      const dayDate = new Date(weekStart);
      dayDate.setDate(dayDate.getDate() + srcDayIdx);
      const gridOriginMs = new Date(
        dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), GRID_START_HOUR
      ).getTime();

      const laneBlocks = computeLaneLayout(dayItems, gridOriginMs, HOUR_HEIGHT, 18);
      laneBlocks.forEach((lb) => result.push({ ...lb, dayIdx: displayIdx }));
    });

    return result;
  }, [dayBuckets, weekStart, threeDayStart, threeDayIndices]);

  // Mobile: the 3 visible columns' all-day chips, sliced from the week's.
  const allDayThreeDay = useMemo(
    () => threeDayIndices.map((srcIdx) => allDayByDay[srcIdx] || []),
    [threeDayIndices, allDayByDay]
  );

  // Weather lookup: { byZone, fetchedByZone } built once per weather fetch.
  // Joined per block by item.zone + the Central day of the block's startMs.
  const weatherLookup = useMemo(() => buildWeatherLookup(zoneWeather), [zoneWeather]);

  const handleEditClick = (item) => {
    setSelectedItem(null);
    setEditingItem(item);
    setEditModalOpen(true);
  };

  const handleEventUpdated = () => {
    setEditModalOpen(false);
    setEditingItem(null);
    fetchWeekData();
  };

  const handleGridMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + (gridBodyRef.current?.scrollTop || 0);
    const hourIdx = Math.floor(y / HOUR_HEIGHT);
    setHoveredHour(hourIdx >= 0 && hourIdx < GRID_HOURS ? hourIdx : null);
  };

  const handleGridClick = (e) => {
    if (!weekStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + (gridBodyRef.current?.scrollTop || 0);
    const x = e.clientX - rect.left;
    // Ignore clicks on the hour label area
    if (x < HOUR_LABEL_WIDTH) return;

    // Determine which day was clicked
    const gridWidth = rect.width - HOUR_LABEL_WIDTH;
    const relX = x - HOUR_LABEL_WIDTH;
    const dayIdx = Math.min(Math.floor((relX / gridWidth) * 7), 6);

    const hourOffset = Math.floor(y / HOUR_HEIGHT);
    const clickedHour = (GRID_START_HOUR + hourOffset) % 24;

    const dayDate = new Date(weekStart);
    dayDate.setDate(dayDate.getDate() + dayIdx);
    const start = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), clickedHour, 0, 0);
    const end = new Date(start.getTime() + 4 * 3600000);

    setCreateEventStartMs(start.getTime().toString());
    setCreateEventEndMs(end.getTime().toString());
    setCreateEventType("job");
    setCreateEventOpen(true);
  };

  const handleGridClickMobile = (e) => {
    if (!threeDayStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + (gridBodyRef.current?.scrollTop || 0);
    const x = e.clientX - rect.left;
    if (x < HOUR_LABEL_WIDTH) return;

    const gridWidth = rect.width - HOUR_LABEL_WIDTH;
    const relX = x - HOUR_LABEL_WIDTH;
    const displayDayIdx = Math.min(Math.floor((relX / gridWidth) * MOBILE_DAY_COUNT), MOBILE_DAY_COUNT - 1);

    const hourOffset = Math.floor(y / HOUR_HEIGHT);
    const clickedHour = (GRID_START_HOUR + hourOffset) % 24;

    const dayDate = new Date(threeDayStart);
    dayDate.setDate(dayDate.getDate() + displayDayIdx);
    const start = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), clickedHour, 0, 0);
    const end = new Date(start.getTime() + 4 * 3600000);

    setCreateEventStartMs(start.getTime().toString());
    setCreateEventEndMs(end.getTime().toString());
    setCreateEventType("job");
    setCreateEventOpen(true);
  };

  const handleCreateEventDone = () => {
    setCreateEventOpen(false);
    fetchWeekData();
  };

  // Header buttons: "Create" seeds a job with no prefilled times; "SIX" seeds
  // a personal event. Both clear any grid-click prefill.
  const openCreate = (type) => {
    setCreateEventStartMs("");
    setCreateEventEndMs("");
    setCreateEventType(type);
    setCreateEventOpen(true);
  };

  // "web:avail-sooner" walkthrough: open this week view's own Create Event
  // form (UI state only — opening it reads the user list, nothing else).
  useTourAction("calendar-open-create", () => openCreate("job"));

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;

  return (
    <>
      <BdrModal
        open={open}
        onClose={onClose}
        variant="panel"
        maxWidth="1400px"
        showHeader={false}
        ariaLabel="Week schedule"
        panelClassName="cal-tall-panel"
        /* A walkthrough may close the calendar (tours/tourModals.js): onClose
           is CalendarPage.handleClose -> the navbar's setCalendarOpen(false)
           + setCalendarFocus(null), state only. */
        tourTag="calendar-week"
      >
        <div className={`week-modal-header${isMobile ? " week-modal-header--mobile" : ""}`}>
          {/* Mobile: stacked layout — pickers on row 1, nav on row 2 */}
          {isMobile ? (
            <>
              <div className="week-modal-header-pickers-row">
                <h3 ref={pickerRef} className="week-modal-header-label">
                  <span
                    className="calendar-picker-trigger"
                    onClick={() => togglePicker("month")}
                  >
                    {MONTH_NAMES[displayMonth]}
                  </span>{" "}
                  <span
                    className="calendar-picker-trigger"
                    onClick={() => togglePicker("year")}
                  >
                    {displayYear}
                  </span>

                  {pickerOpen === "month" && (
                    <div className="calendar-picker-dropdown calendar-month-picker">
                      {MONTH_NAMES.map((name, i) => (
                        <div
                          key={i}
                          className={`calendar-picker-option${
                            i === displayMonth ? " active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePickMonth(i);
                          }}
                        >
                          {name}
                        </div>
                      ))}
                    </div>
                  )}

                  {pickerOpen === "year" && (
                    <div
                      className="calendar-picker-dropdown calendar-year-picker"
                      ref={yearPickerRef}
                    >
                      {Array.from({ length: 21 }, (_, i) => {
                        const year = displayYear - 10 + i;
                        return (
                          <div
                            key={year}
                            className={`calendar-picker-option${
                              year === displayYear ? " active" : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePickYear(year);
                            }}
                          >
                            {year}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </h3>
                <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
                  <CloseIcon />
                </IconButton>
              </div>
              <div className="week-modal-header-nav week-modal-header-nav--mobile">
                <button className="week-nav-btn" onClick={handlePrevThreeDays}>
                  &#8249;
                </button>
                <span className="week-modal-three-day-label">{getThreeDayHeaderLabel()}</span>
                <button className="week-nav-btn" onClick={handleNextThreeDays}>
                  &#8250;
                </button>
              </div>
              <div className="week-modal-header-actions week-modal-header-actions--mobile">
                {isAdmin && (
                  <EventFilterToggle value={eventFilter} onChange={onEventFilterChange} />
                )}
                <button
                  className={`calendar-week-btn${showAIPanel ? " active-ai" : ""}`}
                  onClick={() => setShowAIPanel(!showAIPanel)}
                >
                  {showAIPanel ? "Close AI" : "AI Assign"}
                </button>
                <button className="calendar-week-btn" onClick={onSwitchToMonth}>
                  Month
                </button>
                {onSwitchToDay && (
                  <button className="calendar-week-btn" onClick={onSwitchToDay}>
                    Day
                  </button>
                )}
                <button
                  className="calendar-create-btn bdr-grad-cal-green"
                  onClick={() => openCreate("job")}
                  data-tour="calendar-create-event"
                >
                  Create +
                </button>
                {isAdmin && (
                  <button
                    className="calendar-create-btn bdr-grad-cal-green calendar-six-btn"
                    title="Personal event"
                    onClick={() => openCreate("personal")}
                  >
                    SIX
                    <HearingIcon sx={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
            </>
          ) : (
            /* Desktop: original single-row layout (unchanged) */
            <>
              <div className="week-modal-header-nav">
                <button className="week-nav-btn" onClick={handlePrevWeek}>
                  &#8249;
                </button>
                <h3 ref={pickerRef} className="week-modal-header-label">
                  <span>{getHeaderLabel()}</span>
                  <span className="week-header-divider">|</span>
                  <span
                    className="calendar-picker-trigger"
                    onClick={() => togglePicker("month")}
                  >
                    {MONTH_NAMES[displayMonth]}
                  </span>{" "}
                  <span
                    className="calendar-picker-trigger"
                    onClick={() => togglePicker("year")}
                  >
                    {displayYear}
                  </span>

                  {pickerOpen === "month" && (
                    <div className="calendar-picker-dropdown calendar-month-picker">
                      {MONTH_NAMES.map((name, i) => (
                        <div
                          key={i}
                          className={`calendar-picker-option${
                            i === displayMonth ? " active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePickMonth(i);
                          }}
                        >
                          {name}
                        </div>
                      ))}
                    </div>
                  )}

                  {pickerOpen === "year" && (
                    <div
                      className="calendar-picker-dropdown calendar-year-picker"
                      ref={yearPickerRef}
                    >
                      {Array.from({ length: 21 }, (_, i) => {
                        const year = displayYear - 10 + i;
                        return (
                          <div
                            key={year}
                            className={`calendar-picker-option${
                              year === displayYear ? " active" : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePickYear(year);
                            }}
                          >
                            {year}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </h3>
                <button className="week-nav-btn" onClick={handleNextWeek}>
                  &#8250;
                </button>
              </div>
              <div className="week-modal-header-actions">
                {isAdmin && (
                  <EventFilterToggle value={eventFilter} onChange={onEventFilterChange} />
                )}
                <button
                  className={`calendar-week-btn${showAIPanel ? " active-ai" : ""}`}
                  onClick={() => setShowAIPanel(!showAIPanel)}
                >
                  {showAIPanel ? "Close AI" : "AI Assign"}
                </button>
                <button className="calendar-week-btn" onClick={onSwitchToMonth}>
                  View Month
                </button>
                {onSwitchToDay && (
                  <button className="calendar-week-btn" onClick={onSwitchToDay}>
                    View Day
                  </button>
                )}
                <button
                  className="calendar-create-btn bdr-grad-cal-green"
                  onClick={() => openCreate("job")}
                  data-tour="calendar-create-event"
                >
                  Create Event +
                </button>
                {isAdmin && (
                  <button
                    className="calendar-create-btn bdr-grad-cal-green calendar-six-btn"
                    title="Personal event"
                    onClick={() => openCreate("personal")}
                  >
                    SIX
                    <HearingIcon sx={{ fontSize: 16 }} />
                  </button>
                )}
                <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
                  <CloseIcon />
                </IconButton>
              </div>
            </>
          )}
        </div>

        {showAIPanel && weekStart && (
          <AIAssignmentPanel
            startMs={new Date(weekStart).getTime().toString()}
            endMs={(() => {
              const sat = new Date(weekStart);
              sat.setDate(sat.getDate() + 6);
              sat.setHours(23, 59, 59, 999);
              return sat.getTime().toString();
            })()}
            showDatePickers={false}
            onClose={() => setShowAIPanel(false)}
          />
        )}

        <div className="cal-modal-body">
          <div className="week-modal-content" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {loading ? (
              <div className="week-modal-loading">
                <AdminBDRSpinner message="Loading..." />
              </div>
            ) : error ? (
              <div className="week-modal-error">{error}</div>
            ) : (
              <>
                {/* Day headers — 3 days on mobile, 7 on desktop */}
                <div className="week-grid-header">
                  <div className="week-grid-hour-spacer" style={{ minWidth: HOUR_LABEL_WIDTH }} />
                  {isMobile
                    ? threeDayStart && threeDayIndices.map((srcIdx, displayIdx) => {
                        const d = new Date(weekStart);
                        d.setDate(d.getDate() + srcIdx);
                        const dStr = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                        return (
                          <div
                            key={displayIdx}
                            className={`week-grid-day-header${dStr === todayStr ? " today" : ""}`}
                          >
                            {DAY_NAMES_SHORT[d.getDay()]} {d.getDate()}
                          </div>
                        );
                      })
                    : weekStart && DAY_NAMES_SHORT.map((name, i) => {
                        const d = new Date(weekStart);
                        d.setDate(d.getDate() + i);
                        const dStr = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                        return (
                          <div
                            key={i}
                            className={`week-grid-day-header${dStr === todayStr ? " today" : ""}`}
                          >
                            {name} {d.getDate()}
                          </div>
                        );
                      })
                  }
                </div>

                {/* All-day (personal "SIX") chips — renders nothing when empty */}
                <AllDayEventStrip
                  days={isMobile ? allDayThreeDay : allDayByDay}
                  hourLabelWidth={HOUR_LABEL_WIDTH}
                  colorFor={personalEventColor}
                  onItemClick={(it) =>
                    setSelectedItem((prev) => (prev === it ? null : it))
                  }
                />

                {/* Time grid */}
                <div
                  className="week-grid-scroll"
                  ref={gridBodyRef}
                  style={{ flex: 1, overflow: "auto" }}
                  data-tour="calendar-week-grid"
                >
                  <div
                    className="week-grid-body"
                    style={{
                      height: TOTAL_GRID_HEIGHT,
                      position: "relative",
                    }}
                    onClick={isMobile ? handleGridClickMobile : handleGridClick}
                    onMouseMove={handleGridMouseMove}
                    onMouseLeave={() => setHoveredHour(null)}
                  >
                    {/* Hour lines and labels */}
                    {Array.from({ length: GRID_HOURS }, (_, h) => (
                      <React.Fragment key={h}>
                        <div className="week-grid-hour-line" style={{ top: h * HOUR_HEIGHT }} />
                        <div
                          className={`week-grid-hour-label${hoveredHour === h ? " hovered" : ""}`}
                          style={{ top: h * HOUR_HEIGHT - 6, width: HOUR_LABEL_WIDTH - 4 }}
                        >
                          {formatHourLabel((GRID_START_HOUR + h) % 24)}
                        </div>
                      </React.Fragment>
                    ))}

                    {/* Day dividers — 2 on mobile, 6 on desktop */}
                    {(isMobile ? [1, 2] : [1, 2, 3, 4, 5, 6]).map((i) => {
                      const visibleDays = isMobile ? MOBILE_DAY_COUNT : 7;
                      return (
                        <div
                          key={`div-${i}`}
                          className="week-grid-day-divider"
                          style={{
                            left: `calc(${HOUR_LABEL_WIDTH}px + ${(i / visibleDays) * 100}% - ${(i / visibleDays) * HOUR_LABEL_WIDTH}px)`,
                          }}
                        />
                      );
                    })}

                    {/* Job blocks — overlapping N+1 lane model */}
                    {(isMobile ? blocksThreeDay : blocks).map((block, idx) => {
                      const visibleDays = isMobile ? MOBILE_DAY_COUNT : 7;
                      const dayPct = 100 / visibleDays;
                      const dayLeft = block.dayIdx * dayPct;
                      // Reserve 8% of day width as a right gutter for click-to-create
                      const usableDayPct = dayPct * 0.92;
                      const lanePct = usableDayPct / block.totalVirtualLanes;
                      const leftPct = dayLeft + block.overlapIndex * lanePct;
                      const widthPct = 2 * lanePct;

                      const isGradient = block.background.startsWith("linear-gradient");
                      // Banner shows for multi-day OR chained (non-vacation) events,
                      // subject to height threshold so tiny blocks don't get crowded.
                      const hasBanner =
                        (block.item._isMultiDayPart || block.isChained) &&
                        block.heightPx >= 16;

                      const hoverStyle = hoverStyleFor(block);
                      // Weather chip: join this block to its zone's forecast for
                      // the Central day of its (day-clamped) startMs. No zone or
                      // no data -> weatherForJob returns day:null -> chip renders
                      // nothing. Icon-only on short blocks (density rule); full
                      // cycling chip when there's vertical room.
                      const { day: weatherDay, fetchedMs: weatherFetchedMs } = weatherForJob(
                        weatherLookup,
                        block.item.zone,
                        block.item.calendarItem.startMs
                      );
                      return (
                        <div
                          key={idx}
                          className={`week-grid-block${block.isCompleted ? " completed" : ""}${hasBanner ? " has-multiday-banner" : ""}${block.isTentative ? " tentative-shimmer" : ""}${block.eventType === "vacation" ? " vacation-stripes" : ""}`}
                          style={{
                            top: block.topPx,
                            height: block.heightPx,
                            left: `calc(${HOUR_LABEL_WIDTH}px + ${leftPct}% - ${(leftPct / 100) * HOUR_LABEL_WIDTH}px)`,
                            width: `calc(${widthPct}% - ${(widthPct / 100) * HOUR_LABEL_WIDTH}px)`,
                            background: isGradient ? block.background : undefined,
                            backgroundColor: !isGradient ? block.background : undefined,
                            zIndex: hoverStyle.zIndex ?? block.overlapIndex + 1,
                            opacity: hoverStyle.opacity,
                            transition: hoverStyle.transition,
                          }}
                          onMouseEnter={() => onHoverEnter(block)}
                          onMouseLeave={onHoverLeave}
                          onClick={(e) => {
                            e.stopPropagation();
                            // For multi-day parts, pass the original (unsegmented)
                            // item so the detail popup shows the full job span,
                            // not just this day's segment.
                            const popupItem = block.item._originalItem || block.item;
                            setSelectedItem(
                              selectedItem === popupItem ? null : popupItem
                            );
                          }}
                        >
                          <div className="week-grid-block-stripe" />
                          {hasBanner && (
                            <MultiDayBanner
                              isFirstDay={block.item._isFirstDay}
                              isLastDay={block.item._isLastDay}
                              workDayIndex={block.item._workDayIndex}
                              totalWorkDays={block.item._totalWorkDays}
                              isChained={block.isChained}
                              eventType={block.eventType}
                              variant="week"
                            />
                          )}
                          <span className="week-grid-block-name">
                            {getBlockLabelPrefix(block.item.calendarItem)}{getBlockLabelName(block.item)}
                          </span>
                          {block.heightPx > 22 && (
                            <span className="week-grid-block-time">
                              {BAGroupTimeFormat(
                                block.item.calendarItem.startMs,
                                true,
                                false
                              )}
                            </span>
                          )}
                          {block.heightPx > 36 && getJobAmountStr(block.item) && (
                            <span className="week-grid-block-amount">
                              {getJobAmountStr(block.item)}
                            </span>
                          )}
                          {block.heightPx > 36 && getCalendarJobCategory(block.item) && (
                            <span className="week-grid-block-jobtype">
                              {getCalendarJobCategory(block.item)}
                            </span>
                          )}
                          {block.heightPx > 28 && getFinisherRange(block.item) && (
                            <span className="week-grid-block-finishers">
                              {getFinisherRange(block.item)}
                            </span>
                          )}
                          {weatherDay && (
                            <span className="week-grid-block-weather">
                              <WeatherChip
                                day={weatherDay}
                                fetchedMs={weatherFetchedMs}
                                iconOnly={block.heightPx <= 36}
                                size={12}
                              />
                            </span>
                          )}
                          {block.isCompleted && (
                            <CheckCircleIcon
                              sx={{
                                fontSize: 10,
                                color: "#00e676",
                                position: "absolute",
                                top: 1,
                                right: 1,
                                zIndex: 3,
                              }}
                            />
                          )}
                          {block.isPending && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                const invId = block.item?.calendarItem?.invoiceId;
                                if (invId) {
                                  setReviewInvoiceId(invId);
                                  setReviewModalOpen(true);
                                }
                              }}
                              style={{
                                position: "absolute",
                                top: 1,
                                right: 1,
                                zIndex: 3,
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <ScheduleIcon sx={{ fontSize: 10, color: "#FF9800" }} />
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Current-time line + pill — visible only when today
                        falls within the displayed range (7-day desktop or
                        3-day mobile). Dims to 0.4 alpha while a block is
                        hovered so the hover spotlight stays dominant. */}
                    <CurrentTimeLine
                      hourHeight={HOUR_HEIGHT}
                      gridStartHour={GRID_START_HOUR}
                      gridHours={GRID_HOURS}
                      hourLabelWidth={HOUR_LABEL_WIDTH}
                      visible={
                        isMobile
                          ? isTodayInRange(threeDayStart, MOBILE_DAY_COUNT)
                          : isTodayInRange(weekStart, 7)
                      }
                      dimmed={isHoverActive}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </BdrModal>

      {/* Detail popup — shared across all calendar surfaces */}
      <EventDetailPopup
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onEdit={handleEditClick}
        onViewInvoice={setViewInvoice}
        onViewQuote={setViewQuote}
      />

      <EditEventModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingItem(null);
        }}
        onEventUpdated={handleEventUpdated}
        item={editingItem}
      />

      {viewInvoice && createPortal(
        <ViewClientInvoiceModal
          selectedInvoice={viewInvoice}
          closeInvoiceModal={() => setViewInvoice(null)}
          queryClient={null}
          emailObjHandler={() => {}}
          changeParentForEmailModal={() => {}}
        />,
        document.body
      )}

      {viewQuote && createPortal(
        <ViewClientQuoteModal
          selectedQuote={viewQuote}
          closeQuoteModal={() => setViewQuote(null)}
          queryClient={null}
          emailObjHandler={() => {}}
          changeParentForEmailModal={() => {}}
        />,
        document.body
      )}

      <CreateEventModal
        open={createEventOpen}
        onClose={() => setCreateEventOpen(false)}
        onEventCreated={handleCreateEventDone}
        passedStartMs={createEventStartMs}
        passedEndMs={createEventEndMs}
        initialEventType={createEventType}
      />

      <JobFormReviewModal
        open={reviewModalOpen}
        onClose={() => { setReviewModalOpen(false); setReviewInvoiceId(null); }}
        invoiceId={reviewInvoiceId}
        onStatusChanged={() => { setReviewModalOpen(false); setReviewInvoiceId(null); fetchWeekData(); }}
      />
    </>
  );
};

export default WeekModal;
